import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllSettings } from '../../services/setting.service';
import { Setting } from '../../types/Settings';
import { SettingName, SettingScope } from '../../constants/Constants';

export interface SettingsState {
  settings?: Setting[] | null;
  readerOpsStatus: Setting | null;
  readerSupplyChainStatus: Setting | null;
  readerUsageTypes: Setting | null;
  equipmentTypes: Setting | null;
  equipmentCategories: Setting | null;
  locationMethod: Setting | null;
  equipmentStatus: Setting | null;
  geofenceTypes: Setting | null;
  geofenceLevel: Setting | null;
  facilityTypes: Setting | null;
  customerTypes: Setting | null;
  opsStatusColor: Setting | null;
  supplyChainStatusColor: Setting | null;
  uldStatus: Setting | null;
  isSettingLoading: boolean;
  taskStatusColor: Setting | null;
  taskStatus: Setting | null;
  taskSeverity: Setting | null;
  pairingValidationStatus: Setting | null;
}

const initialState: SettingsState = {
  settings: null,
  readerOpsStatus: null,
  readerSupplyChainStatus: null,
  readerUsageTypes: null,
  equipmentTypes: null,
  equipmentCategories: null,
  locationMethod: null,
  equipmentStatus: null,
  geofenceTypes: null,
  geofenceLevel: null,
  facilityTypes: null,
  isSettingLoading: false,
  customerTypes: null,
  opsStatusColor: null,
  uldStatus: null,
  taskStatusColor: null,
  taskStatus: null,
  taskSeverity: null,
  supplyChainStatusColor: null,
  pairingValidationStatus: null,
};

export const fetchAllSettings = createAsyncThunk('readers/getAllSettings', async () => await getAllSettings({}));

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updateSettingsState: (state, action: PayloadAction<Setting[] | null>) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSettings.pending, (state) => {
        state.isSettingLoading = true;
      })
      .addCase(fetchAllSettings.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload?.status === 200 && action.payload?.data) {
          const { data } = action.payload;
          state.settings = data; // all settings

          state.readerOpsStatus = data.find((v) => v.name === SettingName.ReaderOpsStatus);
          state.readerSupplyChainStatus = data.find((v) => v.name === SettingName.ReaderSupplyChainStatus && v.scope === SettingScope.static);
          state.readerUsageTypes = data.find((v) => v.name === SettingName.ReaderUsageType && v.scope === SettingScope.static);
          state.equipmentTypes = data.find((v) => v.name === SettingName.EquipmentType && v.scope === SettingScope.static);
          state.equipmentCategories = data.find((v) => v.name === SettingName.EquipmentCategory && v.scope === SettingScope.static);
          state.locationMethod = data.find((v) => v.name === SettingName.LocationMethod && v.scope === SettingScope.static);
          state.equipmentStatus = data.find((v) => v.name === SettingName.EquipmentStatus && v.scope === SettingScope.static);
          state.geofenceTypes = data.find((v) => v.name === SettingName.GeofenceType && v.scope === SettingScope.static);
          state.geofenceLevel = data.find((v) => v.name === SettingName.GeofenceLevel && v.scope === SettingScope.static);
          state.facilityTypes = data.find((v) => v.name === SettingName.FacilityType && v.scope === SettingScope.static);
          state.customerTypes = data.find((v) => v.name === SettingName.CustomerTypes && v.scope === SettingScope.shared);
          state.opsStatusColor = data.find((v) => v.name === SettingName.OpsStatusColor && v.scope === SettingScope.static);
          state.supplyChainStatusColor = data.find((v) => v.name === SettingName.SupplyChainStatusColor && v.scope === SettingScope.static);
          state.uldStatus = data.find((v) => v.name === SettingName.UldStatus && v.scope === SettingScope.static);
          state.taskStatusColor = data.find((v) => v.name === SettingName.TaskStatusColor && v.scope === SettingScope.static);
          state.taskStatus = data.find((v) => v.name === SettingName.TaskStatus && v.scope === SettingScope.static);
          state.taskSeverity = data.find((v) => v.name === SettingName.TaskSeverity && v.scope === SettingScope.static);
          state.pairingValidationStatus = data.find((v) => v.name === SettingName.PairingValidationStatus && v.scope === SettingScope.static);
        }
        state.isSettingLoading = false;
      });
  },
});

export const { updateSettingsState } = settingsSlice.actions;
export default settingsSlice.reducer;
